import React, { useEffect, useState, useCallback, useRef } from 'react';
import { ModalBase, Button, Form, Input, notify } from '@bynder/design-system';
import { useTranslate, Translate } from '@bynder/localization';
import { Group, User } from './types';
import { CSRF } from '../../helpers/csrf';
import { API } from './helpers';
import { UserSearch } from './UserSearch';
import { SelectedUsers } from './SelectedUsers';

interface Props {
	mode: 'add' | 'edit';
	groupId?: string;
	goBack: () => void;
}

export function GroupDetails({ mode, groupId, goBack }: Props) {
	const [group, setGroup] = useState<Group>();
	const [groupNameInput, setGroupNameInput] = useState('');
	const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
	const { translate } = useTranslate();

	const inputRef = useRef<HTMLInputElement>(null);

	async function setGroupDetails(id: string) {
		const groupDetails = await API.getGroupDetails(id);
		setGroup(groupDetails);
		setGroupNameInput(groupDetails.name);
		setSelectedUsers(groupDetails.users);
	}

	function onAddUser(user: User) {
		setSelectedUsers((current) => [{ ...user }, ...current]);
	}

	function onRemoveSelectedUser(user: User) {
		setSelectedUsers((current) => current.filter((selected) => selected.id !== user.id));
	}

	async function onSave() {
		const formData = new FormData();
		formData.append('json', 'true');

		if (group) {
			formData.append('ID', group.id);
		}

		formData.append('grp.csrf', CSRF);
		formData.append('grp.name', groupNameInput.trim());
		formData.append('grp.userIDs', selectedUsers.map((user) => user.id).join(','));

		try {
			if (groupNameInput.trim() === '') {
				throw Error;
			}

			const save = await API.saveGroup(formData);

			if (save) {
				goBack();
			}
		} catch (err) {
			notify({ variant: 'error', title: translate('generalErrorSavingChanges') });
		}
	}

	const hasChanged = useCallback((): boolean => {
		if (JSON.stringify(selectedUsers) !== JSON.stringify(group?.users)) {
			return true;
		}

		if (groupNameInput !== group?.name) {
			return true;
		}

		return false;
	}, [selectedUsers, group, groupNameInput]);

	useEffect(() => {
		if (mode === 'edit' && groupId) {
			setGroupDetails(groupId);
		}
	}, [groupId, mode]);

	useEffect(() => {
		if (inputRef.current) {
			inputRef.current.focus();
		}
	}, [inputRef]);

	return (
		<>
			<ModalBase.Header
				title={
					mode === 'add'
						? translate('workflow.AddGroup')
						: translate('workflow.EditGroup')
				}
				backButton={{ title: translate('generalBack'), onClick: goBack }}
			/>
			<ModalBase.Content>
				<Form>
					<Form.Group>
						<Form.Label htmlFor="group-name-input">
							<Translate id="workflow.Name" />
						</Form.Label>
						<Input
							value={groupNameInput}
							id="group-name-input"
							data-testid="wf-group-management-group-name-input"
							onChange={setGroupNameInput}
							inputRef={inputRef}
						/>
					</Form.Group>
					<Form.Group>
						<Form.Label htmlFor="group-users-input">
							<Translate id="workflow.Users" />
						</Form.Label>
						<UserSearch selectedUsers={selectedUsers} onAddUser={onAddUser} />
					</Form.Group>
				</Form>
				<SelectedUsers
					selectedUsers={selectedUsers}
					onRemoveSelectedUser={onRemoveSelectedUser}
				/>
			</ModalBase.Content>
			<ModalBase.Footer
				actionPrimary={
					<Button
						aria-label="save-group"
						variant="primary"
						onClick={onSave}
						isDisabled={!hasChanged()}
						data-testid="wf-group-management-edit-save"
					>
						<Translate id="workflow.Save" />
					</Button>
				}
				actionSecondary={
					<Button aria-label="cancel" variant="secondary" onClick={goBack}>
						<Translate id="workflow.Cancel" />
					</Button>
				}
			/>
		</>
	);
}
